/**
 * Structured Logger for Drift Control
 * - debug/info: only in development
 * - warn/error: always shown
 * - Keeps last 100 entries in memory for support/debugging
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  error?: string;
}

class Logger {
  private isDev: boolean;
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  constructor() {
    this.isDev = import.meta.env.DEV === true;
  }

  private log(level: LogLevel, message: string, contextOrError?: LogContext | Error) {
    const timestamp = new Date().toISOString();

    let context: LogContext | undefined;
    let error: Error | undefined;

    if (contextOrError instanceof Error) {
      error = contextOrError;
    } else {
      context = contextOrError;
    }

    const entry: LogEntry = {
      level,
      message,
      timestamp,
      context,
      error: error?.message,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) this.logs.shift();

    switch (level) {
      case 'debug':
        if (this.isDev) console.log(`[DEBUG] ${message}`, context || '');
        break;
      case 'info':
        if (this.isDev) console.info(`[INFO] ${message}`, context || '');
        break;
      case 'warn':
        console.warn(`[WARN] ${message}`, context || '');
        break;
      case 'error':
        console.error(`[ERROR] ${message}`, error || context || '');
        break;
    }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error, context?: LogContext) {
    const merged = error ? { ...context, errorMsg: error.message } : context;
    this.log('error', message, error || merged);
  }

  getRecentLogs(count = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  clearLogs() {
    this.logs = [];
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

export const logger = new Logger();

/** Shortcut for hook-level debug logs */
export const logHookAction = (hookName: string, action: string, data?: unknown) => {
  logger.debug(`[${hookName}] ${action}`, { component: hookName, action, ...(data && typeof data === 'object' ? data as Record<string, unknown> : { data }) });
};

/** Shortcut for API call logs */
export const logApiCall = (endpoint: string, status: 'start' | 'success' | 'error', details?: unknown) => {
  if (status === 'error') {
    logger.error(`API call failed: ${endpoint}`, undefined, { endpoint, details } as LogContext);
  } else {
    logger.debug(`API ${status}: ${endpoint}`, { endpoint, status, details } as LogContext);
  }
};

/** Shortcut for performance logs */
export const logPerformance = (operation: string, durationMs: number) => {
  if (durationMs > 1000) {
    logger.warn(`Slow operation: ${operation} (${durationMs}ms)`, { operation, durationMs });
  } else {
    logger.debug(`Perf: ${operation} (${durationMs}ms)`, { operation, durationMs });
  }
};
